import { useEffect, useState, createContext } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import NavHeader from "@/components/custom/NavHeader";
import HeroSection from "@/components/custom/HeroSection";
import AboutSection from "@/components/custom/AboutSection";
import InstagramFeed from "@/components/custom/InstagramFeed";
import AccoladesList from "@/components/custom/AccoladesList";
import LinksSection from "@/components/custom/LinksSection";
import ContactForm from "@/components/custom/ContactForm";
import FooterSection from "@/components/custom/FooterSection";
import AdminPanel from "@/components/custom/AdminPanel";
import ClassLanding from "@/components/custom/ClassLanding";
import useScrollTracker from "@/hooks/useScrollTracker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isClassDomain = window.location.hostname.includes("ludbradleyclass");

function useDocumentMeta() {
  useEffect(() => {
    if (!isClassDomain) return;
    document.title = "Lud Bradley Education | Master Black & Grey Realism";
    const metaUpdates = {
      description: "Learn Black & Grey Realism from an internationally recognized artist with 30+ awards. Masterclass, 1-on-1 Seminar, and In-Person Mentorship.",
      keywords: "tattoo education, black grey realism class, tattoo masterclass, tattoo mentorship, Lud Bradley class, learn realism tattoo, tattoo seminar Vancouver",
      "theme-color": "#0A0A0A",
    };
    const ogUpdates = {
      "og:title": "Lud Bradley Education | Master Black & Grey Realism",
      "og:description": "Learn directly from an award-winning Black & Grey Realism artist. Three programs: Masterclass, 1-on-1 Seminar, In-Person Mentorship.",
      "og:url": "https://ludbradleyclass.com",
      "og:site_name": "Lud Bradley Education",
    };
    const twitterUpdates = {
      "twitter:title": "Lud Bradley Education | Master Black & Grey Realism",
      "twitter:description": "Master Black & Grey Realism. Masterclass, 1-on-1 Seminar, and In-Person Mentorship from an internationally recognized artist.",
    };
    Object.entries(metaUpdates).forEach(([name, content]) => {
      const el = document.querySelector(`meta[name="${name}"]`);
      if (el) el.setAttribute("content", content);
    });
    Object.entries(ogUpdates).forEach(([prop, content]) => {
      const el = document.querySelector(`meta[property="${prop}"]`);
      if (el) el.setAttribute("content", content);
    });
    Object.entries(twitterUpdates).forEach(([name, content]) => {
      const el = document.querySelector(`meta[name="${name}"]`);
      if (el) el.setAttribute("content", content);
    });
    // Swap favicons to class branding
    const ico = document.querySelector('link[rel="icon"][href*=".ico"]');
    if (ico) ico.href = "/favicon-class.ico";
    const f32 = document.getElementById("favicon-32");
    if (f32) f32.href = "/favicon-class-32.png";
    const fApple = document.getElementById("favicon-apple");
    if (fApple) fApple.href = "/favicon-class-180.png";

    const ldScript = document.querySelector('script[type="application/ld+json"]');
    if (ldScript) {
      ldScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        name: "Lud Bradley Education",
        description: "Tattoo education by international award-winning Black and Grey Realism artist Lud Bradley.",
        url: "https://ludbradleyclass.com",
        email: "ludbradley.tattoo@gmail.com",
        address: { "@type": "PostalAddress", streetAddress: "314 6th Street", addressLocality: "New Westminster", addressRegion: "BC", addressCountry: "CA" },
        sameAs: ["https://www.instagram.com/ludbradleytattoo/", "https://ludbradleytattoo.com"],
      });
    }
  }, []);
}

export const SiteContext = createContext(null);

function PortfolioSite() {
  const [adminOpen, setAdminOpen] = useState(false);

  useScrollTracker(API);

  return (
    <>
      <NavHeader onAdminOpen={() => setAdminOpen(true)} />
      <main>
        <section id="home"><HeroSection /></section>
        <section id="about"><AboutSection /></section>
        <section id="work"><InstagramFeed /></section>
        <section id="accolades"><AccoladesList /></section>
        <section id="connect"><LinksSection /></section>
        <section id="contact"><ContactForm /></section>
      </main>
      <FooterSection onAdminOpen={() => setAdminOpen(true)} />
      <AdminPanel open={adminOpen} onClose={() => setAdminOpen(false)} />
    </>
  );
}

export default function App() {
  const [settings, setSettings] = useState(null);
  const [adminToken, setAdminToken] = useState(null);

  useDocumentMeta();

  useEffect(() => {
    axios.get(`${API}/settings`).then(r => setSettings(r.data)).catch(() => {});
    axios.post(`${API}/analytics/visit`).catch(() => {});
  }, []);

  return (
    <SiteContext.Provider value={{ settings, setSettings, adminToken, setAdminToken, API }}>
      <div className="App bg-ink-950 text-[#E5E5E5] font-manrope">
        <BrowserRouter>
          <Routes>
            {isClassDomain ? (
              <Route path="*" element={<ClassLanding />} />
            ) : (
              <>
                <Route path="/class" element={<ClassLanding />} />
                <Route path="*" element={<PortfolioSite />} />
              </>
            )}
          </Routes>
        </BrowserRouter>
        <Toaster theme="dark" />
      </div>
    </SiteContext.Provider>
  );
}
